import { auditLogger, isValidUUID, sanitizeErrorMessage, sanitizeObject } from 'src/utils/security';
import { getSupabase } from 'src/utils/supabase';
import { createClientSchema, UpdateClientInput, updateClientSchema } from 'src/utils/validation/schemas';

import { getCurrentUserId, verifyOwnership, withSecurity } from './secureServiceWrapper';
import { assertCanUseFeature, checkAndIncrementUsageClient, decrementUsageClient } from './subscriptionGuard';

export type ClientStatus = 'active' | 'prospect' | 'inactive' | 'pending';
export type ClientType = 'individual' | 'organization';

export const CLIENT_STATUS_OPTIONS: { value: Exclude<ClientStatus, 'pending'>; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'prospect', label: 'Prospect' },
  { value: 'inactive', label: 'Inactive' },
];

export const CLIENT_TYPE_OPTIONS: { value: ClientType; label: string }[] = [
  { value: 'individual', label: 'Individual' },
  { value: 'organization', label: 'Organization' },
];

export const PRACTICE_AREA_OPTIONS: { value: string; label: string }[] = [
  { value: '', label: 'Not specified' },
  { value: 'Litigation', label: 'Litigation' },
  { value: 'Corporate', label: 'Corporate / Business' },
  { value: 'Family', label: 'Family' },
  { value: 'Real Estate', label: 'Real Estate' },
  { value: 'Estate Planning', label: 'Estate Planning / Wills & Trusts' },
  { value: 'Immigration', label: 'Immigration' },
  { value: 'Criminal', label: 'Criminal' },
  { value: 'Intellectual Property', label: 'Intellectual Property' },
  { value: 'Tax', label: 'Tax' },
  { value: 'Employment', label: 'Employment / Labor' },
  { value: 'Personal Injury', label: 'Personal Injury' },
  { value: 'Bankruptcy', label: 'Bankruptcy' },
  { value: 'Other', label: 'Other' },
];

export const normalizeStatusForDisplay = (
  status: ClientStatus | null | undefined
): Exclude<ClientStatus, 'pending'> => {
  if (status === 'pending') return 'prospect';
  if (status === 'inactive' || status === 'prospect') return status;
  return 'active';
};

export interface Client {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  jurisdiction?: string;
  created_at: string;
  updated_at: string;
  last_activity_at?: string | null;
  profile_id: string;
  law_firm_id: string;
  status: ClientStatus;
  client_type?: ClientType | null;
  address_street?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_postal?: string | null;
  address_country?: string | null;
  practice_area?: string | null;
  opposing_party?: string | null;
  referral_source?: string | null;
  engagement_date?: string | null;
}

export interface CreateClientData {
  name: string;
  email?: string;
  phone?: string;
  jurisdiction?: string;
  status?: ClientStatus;
  profile_id: string;
  law_firm_id?: string;
  client_type?: ClientType | null;
  address_street?: string | null;
  address_city?: string | null;
  address_state?: string | null;
  address_postal?: string | null;
  address_country?: string | null;
  practice_area?: string | null;
  opposing_party?: string | null;
  referral_source?: string | null;
  engagement_date?: string | null;
}

export const clientService = {
  async getClients(profileId: string): Promise<Client[]> {
    return withSecurity(
      async () => {
        if (!isValidUUID(profileId)) {
          throw new Error('Invalid profile ID');
        }

        const supabase = await getSupabase();
        const { data, error } = await supabase
          .from('clients')
          .select('*')
          .eq('profile_id', profileId)
          .order('last_activity_at', { ascending: false, nullsFirst: false })
          .order('created_at', { ascending: false });

        if (error) {
          throw new Error(sanitizeErrorMessage(error));
        }

        return data || [];
      },
      { requireAuth: true }
    );
  },

  async getClient(clientId: string): Promise<Client> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          throw new Error('Invalid client ID');
        }

        await verifyOwnership('clients', clientId);

        const supabase = await getSupabase();
        const { data, error } = await supabase.from('clients').select('*').eq('id', clientId).single();

        if (error) {
          throw new Error(sanitizeErrorMessage(error));
        }

        return data;
      },
      { requireAuth: true }
    );
  },

  async createClient(clientData: CreateClientData): Promise<Client> {
    return withSecurity(
      async () => {
        const { profile_id, law_firm_id, ...formData } = clientData;

        if (!isValidUUID(profile_id)) {
          throw new Error('Invalid profile ID');
        }
        if (law_firm_id && !isValidUUID(law_firm_id)) {
          throw new Error('Invalid law firm ID');
        }

        await assertCanUseFeature('clients');

        const usage = await checkAndIncrementUsageClient('clients');
        if (!usage.allowed) {
          throw new Error('Client limit reached. Please upgrade your plan.');
        }

        const sanitized = sanitizeObject(formData as unknown as Record<string, unknown>, {
          placeNameKeys: ['jurisdiction'],
        });
        const validated = createClientSchema.parse(sanitized);

        const insertData: Record<string, unknown> = {
          ...validated,
          profile_id,
          ...(law_firm_id ? { law_firm_id } : {}),
        };

        const supabase = await getSupabase();
        const { data, error } = await supabase.from('clients').insert([insertData]).select().single();

        if (error) {
          await decrementUsageClient('clients').catch(() => {});
          throw new Error(sanitizeErrorMessage(error));
        }

        return data;
      },
      {
        requireAuth: true,
        rateLimitKey: 'client_create',
        rateLimitMax: 50,
      }
    );
  },

  async updateClient(clientId: string, updates: UpdateClientInput): Promise<Client> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          throw new Error('Invalid client ID');
        }

        const isOwner = await verifyOwnership('clients', clientId);
        if (!isOwner) {
          throw new Error('Access denied');
        }

        const sanitized = sanitizeObject(updates as unknown as Record<string, unknown>, {
          placeNameKeys: ['jurisdiction'],
        });
        const validated = updateClientSchema.parse(sanitized);

        const supabase = await getSupabase();
        const { data, error } = await supabase.from('clients').update(validated).eq('id', clientId).select().single();

        if (error) {
          throw new Error(sanitizeErrorMessage(error));
        }

        return data;
      },
      { requireAuth: true }
    );
  },

  async deleteClient(clientId: string): Promise<void> {
    return withSecurity(
      async () => {
        if (!isValidUUID(clientId)) {
          throw new Error('Invalid client ID');
        }

        const isOwner = await verifyOwnership('clients', clientId);
        if (!isOwner) {
          throw new Error('Access denied');
        }

        const supabase = await getSupabase();
        const { error } = await supabase.from('clients').delete().eq('id', clientId);

        if (error) {
          throw new Error(sanitizeErrorMessage(error));
        }
      },
      { requireAuth: true }
    );
  },

  async subscribeToClientUpdates(
    profileId: string,
    onUpdate: (payload: unknown) => void,
    onError?: (error: Error) => void
  ) {
    if (!isValidUUID(profileId)) {
      throw new Error('Invalid profile ID');
    }

    const currentUserId = await getCurrentUserId();
    if (currentUserId !== profileId) {
      auditLogger.log('OWNERSHIP_VIOLATION', { reason: 'subscribe_mismatch', service: 'client' });
      return null;
    }

    const supabase = await getSupabase();

    const channel = supabase
      .channel('client_updates')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'clients',
          filter: `profile_id=eq.${profileId}`,
        },
        (payload) => {
          onUpdate(payload);
        }
      )
      .subscribe((status) => {
        if (status === 'CHANNEL_ERROR' && onError) {
          onError(new Error('Failed to subscribe to client updates'));
        }
      });

    return channel;
  },
};
